/**
 * HTML -> plaintext for the `text/plain` alternative part.
 *
 * Deliberately string-based rather than DOM-based so the Cloud Function can
 * re-render the exact same output server-side without a DOM shim.
 */

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  '#39': "'",
  nbsp: ' ',
  mdash: '—',
  ndash: '–',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  // Accented Latin-1 — guest names routinely carry these, and some sources
  // paste the named form rather than the character.
  eacute: 'é',
  egrave: 'è',
  ecirc: 'ê',
  euml: 'ë',
  aacute: 'á',
  agrave: 'à',
  acirc: 'â',
  auml: 'ä',
  aring: 'å',
  atilde: 'ã',
  iacute: 'í',
  igrave: 'ì',
  icirc: 'î',
  iuml: 'ï',
  oacute: 'ó',
  ograve: 'ò',
  ocirc: 'ô',
  ouml: 'ö',
  otilde: 'õ',
  oslash: 'ø',
  uacute: 'ú',
  ugrave: 'ù',
  ucirc: 'û',
  uuml: 'ü',
  ntilde: 'ñ',
  ccedil: 'ç',
  szlig: 'ß',
  Eacute: 'É',
  Aacute: 'Á',
  Iacute: 'Í',
  Oacute: 'Ó',
  Uacute: 'Ú',
  Ntilde: 'Ñ',
  Ccedil: 'Ç',
};

export function decodeEntities(input: string): string {
  return input.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (full, code: string) => {
    if (Object.prototype.hasOwnProperty.call(ENTITIES, code)) return ENTITIES[code];
    if (code.startsWith('#x') || code.startsWith('#X')) {
      const value = parseInt(code.slice(2), 16);
      return Number.isNaN(value) ? full : String.fromCodePoint(value);
    }
    if (code.startsWith('#')) {
      const value = parseInt(code.slice(1), 10);
      return Number.isNaN(value) ? full : String.fromCodePoint(value);
    }
    return full;
  });
}

/**
 * Control characters stand in for the angle brackets around a bare URL until
 * after all real tags have been stripped. They cannot occur in HTML text.
 */
const OPEN = '';
const CLOSE = '';

export function htmlToText(html: string): string {
  // Any of these already present in the input would corrupt the restore step.
  let out = html.replace(/[]/g, '');

  // Drop content that has no plaintext meaning at all.
  out = out.replace(/<(script|style|head)[^>]*>[\s\S]*?<\/\1>/gi, '');

  // Horizontal rules read as a visual separator.
  out = out.replace(/<hr\b[^>]*>/gi, '\n\n----------\n\n');
  out = out.replace(/<br\s*\/?>/gi, '\n');

  // List items become dashes before the tags are stripped. Rich editors wrap
  // each item's content in a block (e.g. `<li><p>Fishing</p></li>`), so flatten
  // any block tags inside the item first — otherwise their boundaries would
  // open a blank line between every bullet.
  out = out.replace(/<li\b[^>]*>([\s\S]*?)<\/li>/gi, (_full, inner: string) => {
    const flat = inner
      .replace(/<\/(p|div|h[1-6]|blockquote)\s*>/gi, ' ')
      .replace(/<(p|div|h[1-6]|blockquote)\b[^>]*>/gi, '');
    return `\n- ${flat}`;
  });

  // Preserve link targets — a plaintext reader can't follow an <a href>.
  // The angle brackets are emitted as sentinels and restored after tag
  // stripping, otherwise the stripper would treat `<https://…>` as a tag and
  // delete the URL.
  out = out.replace(
    /<a\b[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
    (_full, href: string, label: string) => {
      const text = decodeEntities(label.replace(/<[^>]+>/g, '')).trim();
      const url = decodeEntities(href).trim();
      if (!text) return `${OPEN}${url}${CLOSE}`;
      // Skip the duplication when the label already is the URL.
      if (text === url || url === `mailto:${text}`) return text;
      return `${text} ${OPEN}${url}${CLOSE}`;
    },
  );

  // Block-level boundaries become paragraph breaks.
  out = out.replace(
    /<\/(p|div|h[1-6]|ul|ol|table|tr|blockquote|section|article)\s*>/gi,
    '\n\n',
  );
  out = out.replace(/<\/(td|th)\s*>/gi, '\t');

  out = out.replace(/<[^>]+>/g, '');
  out = decodeEntities(out);

  // Restore the link brackets now that nothing else will be read as a tag.
  out = out.split(OPEN).join('<').split(CLOSE).join('>');

  // Tidy up: trailing spaces, runaway blank lines, leading/trailing padding.
  out = out.replace(/\r\n?/g, '\n');
  out = out.replace(/[ \t]+\n/g, '\n');
  out = out.replace(/\n{3,}/g, '\n\n');
  out = out.replace(/[ \t]{2,}/g, ' ');

  return out.trim();
}
